import React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Link } from "@mui/material";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { h2Rule, paragraph, ctaSx, darkPanel, fadeUp } from "../theme/marketingStyles";

/** /voor-werkgevers (+ /voor-organisaties) — B2B-pagina: usage-based model
 *  (€5/€2 per sessie + jaarfee-tiers), privacy-firewall (k>=10, geen
 *  HR-zichtbaarheid, cap -> zelf betalen), barometer en onboarding-kit.
 *  Bedragen spiegelen speakhere-api/src/services/employerBilling.ts en
 *  docs/onboarding-kit/prijsblad.md — richtprijzen, het ondertekende
 *  voorstel primeert. Hero = het donkere, warme paneel van de homepage-teaser
 *  (continuïteit voor de B2B-bezoeker). */
export default function ForOrganisations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const props = [
    { icon: <TrendingDownRoundedIcon />, title: t("forOrg.prop1Title"), body: t("forOrg.prop1Body") },
    { icon: <LockRoundedIcon />, title: t("forOrg.prop2Title"), body: t("forOrg.prop2Body") },
    { icon: <InsightsRoundedIcon />, title: t("forOrg.prop3Title"), body: t("forOrg.prop3Body") },
  ];

  const bullets = (keys: string[]) => (
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li::marker": { color: "var(--primary-300)" } }}>
      {keys.map((k) => (
        <Typography key={k} component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t(k)}</Typography>
      ))}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* Donker, warm hero-paneel — dezelfde beeldtaal als de
          werkgevers-teaser op de homepage. */}
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 } }}>
        <Box sx={{ ...darkPanel, p: { xs: 4, sm: 9 }, textAlign: "center", ...fadeUp(0) }}>
          <Typography variant="h1" sx={{ fontSize: { xs: "2.4rem", sm: "3.25rem" }, letterSpacing: "-0.015em", mb: 2, color: "#EDE6DA" }}>
            {t("forOrg.title")}
          </Typography>
          <Typography sx={{ fontSize: "1.15rem", maxWidth: 620, mx: "auto", mb: 4, color: "#B3A998", lineHeight: 1.65 }}>
            {t("forOrg.subtitle")}
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/contact")} sx={{ minWidth: 240, ...ctaSx }}>
            {t("forOrg.cta")}
          </Button>
          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "#B3A998" }}>{t("forOrg.ctaNote")}</Typography>
        </Box>
      </Container>

      {/* Propositie-strip: geen kaartjes — ruimte doet het werk. */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, sm: 8 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: { xs: 3, sm: 5 }, ...fadeUp(120) }}>
          {props.map((v) => (
            <Box key={v.title} sx={{ display: "flex", gap: 1.75, flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, textAlign: { xs: "center", sm: "left" } }}>
              <Box sx={{ color: "var(--accent-700)", mt: 0.25, "& svg": { fontSize: 28 } }}>{v.icon}</Box>
              <Box>
                <Typography variant="h3" sx={{ fontSize: "1.1rem", mb: 0.25 }}>{v.title}</Typography>
                <Typography sx={{ m: 0, fontSize: ".95rem", color: "text.secondary" }}>{v.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ pb: { xs: 8, sm: 11 } }}>
        <Typography variant="h2" sx={{ ...h2Rule, mt: 0 }}>{t("forOrg.modelTitle")}</Typography>
        <Typography sx={paragraph}>{t("forOrg.model1")}</Typography>
        {bullets(["forOrg.modelB1", "forOrg.modelB2", "forOrg.modelB3"])}
        {/* Prijzen bewust niet publiek (beslissing 2026-07-02): op maat via een voorstel. */}
        <Typography sx={{ ...paragraph, fontSize: ".9rem" }}>{t("forOrg.tiersNote")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("forOrg.capTitle")}</Typography>
        <Typography sx={paragraph}>{t("forOrg.cap1")}</Typography>
        <Typography sx={paragraph}>{t("forOrg.cap2")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("forOrg.privacyTitle")}</Typography>
        <Typography sx={paragraph}>{t("forOrg.privacy1")}</Typography>
        {bullets(["forOrg.privacyB1", "forOrg.privacyB2", "forOrg.privacyB3"])}
        <Typography sx={paragraph}>
          {t("forOrg.privacy2")}{" "}
          <Link component={RouterLink} to="/privacystandaard">{t("forOrg.privacyLink")}</Link>
        </Typography>

        <Typography variant="h2" sx={h2Rule}>{t("forOrg.barometerTitle")}</Typography>
        <Typography sx={paragraph}>{t("forOrg.barometer1")}</Typography>
        <Typography sx={paragraph}>
          {t("forOrg.barometer2")}{" "}
          <Link component={RouterLink} to="/welzijnsbarometer">{t("forOrg.barometerLink")}</Link>
        </Typography>

        <Typography variant="h2" sx={h2Rule}>{t("forOrg.onboardTitle")}</Typography>
        <Typography sx={paragraph}>{t("forOrg.onboard1")}</Typography>
        {bullets(["forOrg.onboardB1", "forOrg.onboardB2", "forOrg.onboardB3"])}

        <Box sx={{ mt: 7, textAlign: "center" }}>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>{t("forOrg.ctaLead")}</Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/contact")} sx={{ minWidth: 240, ...ctaSx }}>
            {t("forOrg.cta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
